import React from "react";
import { ArrowUpRight, ShieldCheck, CalendarClock, BadgeCheck } from "lucide-react";

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-20 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-24">
        <div className="lg:col-span-7">
          <p
            data-testid="about-eyebrow"
            className="mb-6 inline-block font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600"
          >
            — Who We Are
          </p>
          <h2
            data-testid="about-heading"
            className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
          >
            We don't just clean restrooms.
            <br />
            <span className="text-blue-600">We protect reputations.</span>
          </h2>
          <div className="mt-8 space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
            <p>
              Deep Clean Restroom Services specializes in high-quality,
              commercial restroom sanitation. We go beyond surface cleaning to
              deliver a truly deep, hygienic clean that keeps your facility
              fresh, safe, and welcoming.
            </p>
            <p>
              We proudly serve businesses that understand the importance of a
              spotless restroom — because your customers notice. Every detail.
              Every time.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 border-t border-slate-200 pt-8 sm:grid-cols-3">
            {[
              { icon: ShieldCheck, value: "Fully", label: "Insured" },
              { icon: CalendarClock, value: "Flexible", label: "Scheduling" },
              { icon: BadgeCheck, value: "100%", label: "Satisfaction Guarantee" },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={`about-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="flex items-start gap-3"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                  <s.icon size={18} strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
                    {s.value}
                  </p>
                  <p className="mt-0.5 font-body text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {s.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          {/* Visual replacement for the previous photo: a designed callout block. */}
          <div
            data-testid="about-callout"
            className="relative h-full overflow-hidden rounded-md border border-slate-200 bg-slate-950 p-8 text-white md:p-10"
          >
            <div
              className="absolute inset-0 opacity-50"
              aria-hidden="true"
              style={{
                background:
                  "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent 70%), radial-gradient(60% 60% at 0% 100%, rgba(0,85,255,0.45), transparent 70%)",
              }}
            />
            <div className="relative">
              <p className="font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-300">
                Our Promise
              </p>
              <p className="mt-6 font-heading text-3xl font-black leading-tight tracking-tighter text-white md:text-4xl">
                Spotless. Sanitized.
                <br />
                <span className="text-cyan-300">Guaranteed every visit.</span>
              </p>
              <p className="mt-6 font-body text-sm leading-relaxed text-slate-300 md:text-base">
                If anything isn't perfect when we're done, we make it right —
                no questions asked. That's the Deep Clean standard.
              </p>
              <p className="mt-8 inline-flex items-center gap-1.5 font-body text-sm font-semibold text-cyan-300">
                Ask about our service plans
                <ArrowUpRight size={14} strokeWidth={2.2} />
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
